import React, { useEffect, useState } from "react";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Loading from "../components/Loading";
import Modal from "../components/Modal";
import Field from "../components/Field";

export default function BillingPage() {

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [username, setUsername] = useState(null);
    const [password, setPassword] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [existingUsername, setExistingUsername] = useState(null);

    const STATUS_LABELS = {
        draft: "brouillon",
        sent: "envoyé",
        paid: "payé",
        overdue: "en retard",
        cancelled: "annulé"
    }

    const STATUS_CLASSES = {
        draft: "light",
        sent: "primary",
        paid: "success",
        overdue: "danger",
        cancelled: "secondary"
    }

    const PLAN_LABELS = {
        standard: "Standard",
        route: "Route",
        logistics: "Logistics"
    }

    function formatDate(isoString) {
        if (!isoString) return "";
        return moment(isoString).format("ddd DD MMM YYYY");
    }

    function formatAmount(cents) {
        return (cents / 100).toFixed(2) + " €";
    }

    async function fetchInvoices() {
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/invoices`);
            if (!response.ok) {
                const err = await response.json().catch(() => null);
                setError(err?.error || "Erreur lors du chargement des factures");
                return;
            }
            const data = await response.json();
            setInvoices(data);
        } catch (err) {
            setError("Erreur lors du chargement des factures");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchInvoices();
    }, []);

    useEffect(() => {
        if (!showInviteModal) return;
        async function fetchCredentials() {
            try {
                const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/client_credentials`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.vintedUsername) {
                        setExistingUsername(data.vintedUsername);
                    }
                }
            } catch (err) {
                // Chargement secondaire, pas de feedback nécessaire
            }
        }
        fetchCredentials();
    }, [showInviteModal]);

    async function handlePostCredentials() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/client_credentials`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    vintedUsername: username,
                    plainVintedPassword: password
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setError(errorData.violations?.[0]?.message ?? "Erreur lors de l'envoi des identifiants");
                return;
            }
            setExistingUsername(username);
            setUsername(null);
            setPassword(null);
            setShowInviteModal(false);
        } catch (error) {
            setError("Erreur lors de l'envoi des identifiants");
        }
    }

    return (
        <>
            <Modal
                title={"Identifiant"}
                body={
                    <>
                        {existingUsername && (
                            <p className="mb-3">Compte lié : <strong>{existingUsername}</strong></p>
                        )}
                        <Field
                            name="username"
                            label="Adresse e-mail"
                            type="mail"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                        <Field
                            name="password"
                            label="Mot de passe"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </>
                }
                button={{
                    close:"Annuler",
                    valid:"Valider"
                }}
                showModal={showInviteModal}
                setShowModal={setShowInviteModal}
                onSubmit={handlePostCredentials}
            />
            <div className="card mb-4">
                <div className="card-header py-3">
                    <div className="row py-3">
                        <div className="col-sm-4 text-center text-sm-start">
                            <h5 className="mb-4 mb-sm-0 mt-2">
                                Facturation
                            </h5>
                        </div>
                        <div className="col-sm-8 text-center text-sm-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary me-2"
                                onClick={() => setShowInviteModal(true)}
                            >
                                Lier
                                <i className="fas fa-link ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <Loading />
                ) : error ? (
                    <div className="card-body">
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    </div>
                ) : !invoices?.length ? (
                    <div className="card-body">
                        <p>Aucune facture disponible</p>
                    </div>
                ) : (
                    <div className="card-body py-5 px-5">
                        <table className="table table-hover align-middle mb-0 bg-white text-center" id="tableInvoices">
                            <thead className="bg-light">
                                <tr>
                                    <th>#</th>
                                    <th>Statut</th>
                                    <th>Abonnement</th>
                                    <th>Montant</th>
                                    <th>Date d'émission</th>
                                    <th>Date d'échéance</th>
                                    <th>Date de paiement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id}>
                                        <td>{invoice.invoiceNumber}</td>
                                        <td>
                                            <span className={"badge badge-" + STATUS_CLASSES[invoice.status]}>{STATUS_LABELS[invoice.status]}</span>
                                        </td>
                                        <td>{PLAN_LABELS[invoice.plan] ?? invoice.plan}</td>
                                        <td>{formatAmount(invoice.amountCents)}</td>
                                        <td>{formatDate(invoice.issuedAt)}</td>
                                        <td>{formatDate(invoice.dueAt)}</td>
                                        <td>{formatDate(invoice.paidAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}
